import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { authApi, referralApi, demoApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Mail, Lock, Gift, Play, Loader2 } from 'lucide-react';

const Login = ({ isSignup = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState(isSignup ? 'signup' : 'login');
  const [step, setStep] = useState('email'); // 'email' or 'otp'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [debugOtp, setDebugOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDemoLoading, setIsDemoLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [purchaseMessage, setPurchaseMessage] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralApplied, setReferralApplied] = useState(false);

  // Check for referral code, error in URL params, and pending plan
  useEffect(() => {
    const error = searchParams.get('error');
    const urlMode = searchParams.get('mode');
    const ref = searchParams.get('ref');
    
    if (error === 'account_not_found') {
      setErrorMessage('Account not found. Please sign up first or use a different email.');
    }
    
    // Check if mode is specified in URL
    if (urlMode === 'signup' || isSignup) {
      setMode('signup');
    }
    
    // Capture referral code from URL
    if (ref) {
      setReferralCode(ref);
      localStorage.setItem('referral_code', ref);
      setMode('signup'); // Always signup mode when coming from referral
    } else {
      // Check localStorage for previously stored referral code
      const storedRef = localStorage.getItem('referral_code');
      if (storedRef) {
        setReferralCode(storedRef);
      }
    }
    
    // If there's a pending plan, default to signup mode and show message
    if (localStorage.getItem('pending_plan')) {
      setMode('signup');
      setPurchaseMessage('Please sign up to continue with your purchase');
    }
  }, [searchParams, isSignup]);

  const handleGoogleLogin = () => {
    // Start Google OAuth flow for login/signup
    const clientId = '117512974971-gsslagvurcu0japtookt56j1u033u6dm.apps.googleusercontent.com';
    const redirectUri = `${window.location.origin}/api/auth/google/callback`;
    const scope = 'openid email profile';
    const responseType = 'code';
    // Pass signup mode in state parameter
    const state = mode === 'signup' ? 'signup' : 'login';
    
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=${responseType}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&access_type=offline` +
      `&prompt=consent` +
      `&state=${state}`;
    
    window.location.href = googleAuthUrl;
  };

  const handleMicrosoftLogin = () => {
    // Microsoft OAuth for login/signup
    const clientId = '91910a71-f7c4-4d92-8ac8-7c0064f23dd7';
    const redirectUri = `${window.location.origin}/api/auth/microsoft/callback`;
    const scope = 'openid email profile User.Read';
    const responseType = 'code';
    // Pass signup mode in state parameter
    const state = mode === 'signup' ? 'signup' : 'login';
    
    const microsoftAuthUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_type=${responseType}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&response_mode=query` +
      `&state=${state}`;
    
    window.location.href = microsoftAuthUrl;
  };

  // Demo account login - one click with sample data
  const handleDemoLogin = async () => {
    setIsDemoLoading(true);
    try {
      // First, setup demo account with sample data
      await demoApi.setup();
      
      // Then send OTP (which uses fixed code for demo)
      await authApi.sendOtp('demo@subguardhq.com');
      
      // Verify with fixed OTP - this returns the user data
      const response = await authApi.verifyOtp('demo@subguardhq.com', '123456');
      
      // The verify response should include user data
      const userData = response.data.user || {
        email: 'demo@subguardhq.com',
        name: 'Demo User',
        subscription_plan: 'founding',
        onboarding_completed: true,
        is_demo: true
      };
      
      // Login with token and user data
      login(response.data.token, userData);
      
      toast.success('Welcome to the demo! Explore SubGuard with sample data.');
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Demo login error:', error);
      toast.error('Failed to load demo. Please try again.');
    } finally {
      setIsDemoLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.sendOtp(email);
      toast.success('OTP sent to your email!');
      // Display OTP on screen for testing
      if (response.data.debug_otp) {
        setDebugOtp(response.data.debug_otp);
      }
      setStep('otp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.verifyOtp(email, otp, rememberMe, mode === 'signup');
      login(response.data.token, response.data.user);
      
      // Fetch full user data including onboarding status
      const userResponse = await authApi.getMe();
      const fullUser = userResponse.data;
      login(response.data.token, fullUser);
      
      toast.success(mode === 'signup' ? 'Account created! Welcome to SubGuard!' : 'Welcome back!');
      
      // Track Meta Pixel events
      if (typeof window.fbq === 'function') {
        if (mode === 'signup') {
          // Track Lead event for new signups
          window.fbq('track', 'Lead', {
            content_name: 'SubGuard Signup',
            content_category: 'signup'
          });
        }
        // Track CompleteRegistration for all successful logins
        window.fbq('track', 'CompleteRegistration', {
          content_name: mode === 'signup' ? 'New User' : 'Returning User'
        });
      }
      
      // Apply referral code if this is a new signup
      if (mode === 'signup') {
        const storedRef = localStorage.getItem('referral_code');
        if (storedRef && !referralApplied) {
          try {
            await referralApi.applyReferralCode(storedRef);
            toast.success('Referral code applied! 🎉');
            setReferralApplied(true);
            localStorage.removeItem('referral_code');
          } catch (refError) {
            // Don't block signup if referral fails
            console.error('Failed to apply referral:', refError);
          }
        }
      }
      
      // Check for pending plan purchase
      const pendingPlan = localStorage.getItem('pending_plan');
      if (pendingPlan) {
        localStorage.removeItem('pending_plan');
        navigate(`/pricing?select=${pendingPlan}`);
        return;
      }
      
      // Navigate based on onboarding status
      if (!fullUser.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid OTP';
      toast.error(errorMsg);
      if (errorMsg.includes('Account not found')) {
        setStep('email');
        setMode('signup');
        setErrorMessage('Account not found. Switch to Sign Up to create an account.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img 
                src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                alt="SubGuard" 
                className="w-10 h-10 rounded-xl"
              />
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                SubGuard
              </span>
            </button>
          </div>

          <Card className="border-0 shadow-soft-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {step === 'email' 
                  ? (mode === 'signup' ? t.auth.createAccount || 'Create your account' : t.auth.welcomeBack)
                  : t.auth.checkYourEmail}
              </CardTitle>
              <CardDescription className="text-gray-500">
                {step === 'email' 
                  ? (mode === 'signup' ? t.auth.signUpToSave || 'Sign up to start saving on subscriptions' : t.auth.signInManage)
                  : `${t.auth.weSentCode} ${email}`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              {step === 'email' ? (
                <div className="space-y-4">
                  {/* Referral Code Banner */}
                  {referralCode && mode === 'signup' && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                      <Gift className="w-5 h-5 text-amber-600" />
                      <p className="text-sm text-amber-700">
                        <span className="font-medium">Referral code applied:</span> {referralCode}
                      </p>
                    </div>
                  )}
                  
                  {/* Purchase Message */}
                  {purchaseMessage && (
                    <div className="p-3 bg-[#D1FAE5] border border-[#064E3B]/20 rounded-xl">
                      <p className="text-sm text-[#064E3B] font-medium text-center">{purchaseMessage}</p>
                    </div>
                  )}
                  
                  {/* Login/Signup Toggle */}
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-4">
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setErrorMessage(''); setPurchaseMessage(''); }}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                        mode === 'login' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {t.auth.logIn || 'Log In'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMode('signup'); setErrorMessage(''); }}
                      className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition ${
                        mode === 'signup' ? 'bg-white shadow text-gray-900' : 'text-gray-500'
                      }`}
                    >
                      {t.auth.signUp || 'Sign Up'}
                    </button>
                  </div>

                  {/* Microsoft Sign In Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleMicrosoftLogin}
                    className="w-full h-12 rounded-xl border-gray-200 hover:bg-gray-50"
                    data-testid="microsoft-login-btn"
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z"/>
                      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
                      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
                      <path fill="#FFB900" d="M13 13h10v10H13z"/>
                    </svg>
                    {t.auth.continueWithMicrosoft || 'Continue with Microsoft'}
                  </Button>

                  {/* Google Sign In Button */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleLogin}
                    className="w-full h-12 rounded-xl border-gray-200 bg-white hover:bg-gray-50"
                    data-testid="google-login-btn"
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t.auth.continueWithGoogle || 'Continue with Google'}
                  </Button>
                  
                  {/* Google verification notice */}
                  <p className="text-xs text-gray-400 text-center -mt-1">
                    Google sign-in is in verification testing. For reliable access, use Microsoft or email.
                  </p>

                  {/* Try Demo Button */}
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="bg-white px-2 text-gray-400">or explore first</span>
                    </div>
                  </div>
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDemoLogin}
                    disabled={isDemoLoading}
                    className="w-full h-12 rounded-xl border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white transition-colors"
                    data-testid="demo-login-btn"
                  >
                    {isDemoLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    {isDemoLoading ? 'Loading Demo...' : 'Try Demo Account'}
                  </Button>
                  <p className="text-xs text-gray-400 text-center -mt-1">
                    Explore with sample data — no signup needed
                  </p>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">{t.auth.orContinueWithEmail}</span>
                    </div>
                  </div>

                  <form onSubmit={handleSendOtp} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">{t.auth.emailAddress}</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10 h-12 bg-gray-50 border-gray-200 focus:bg-white focus:border-[#064E3B] focus:ring-[#064E3B]/10"
                          data-testid="email-input"
                        />
                      </div>
                    </div>
                    
                    {/* Remember Me */}
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-[#064E3B] focus:ring-[#064E3B]"
                      />
                      <label htmlFor="rememberMe" className="text-sm text-gray-600">
                        {t.auth.rememberMe || 'Remember me for 30 days'}
                      </label>
                    </div>
                    
                    {errorMessage && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-sm text-red-600">{errorMessage}</p>
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                      disabled={isLoading}
                      data-testid="send-otp-btn"
                    >
                      {isLoading ? (t.common.loading || 'Sending...') : (mode === 'signup' ? (t.auth.signUpWithEmail || 'Sign Up with Email') : t.auth.continueWithEmail)}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </form>
                </div>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-6">
                  {/* Display OTP for testing */}
                  {debugOtp && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-sm text-amber-800 font-medium">{t.auth.testModeOtp}</p>
                      <p className="text-2xl font-bold text-amber-900 tracking-widest mt-1" data-testid="debug-otp-display">{debugOtp}</p>
                    </div>
                  )}
                  <div className="space-y-4">
                    <label className="text-sm font-medium text-gray-700">{t.auth.enterCode}</label>
                    <div className="flex justify-center">
                      <InputOTP 
                        maxLength={6} 
                        value={otp} 
                        onChange={handleOtpChange}
                        data-testid="otp-input"
                      >
                        <InputOTPGroup className="gap-2">
                          {[0, 1, 2, 3, 4, 5].map((index) => (
                            <InputOTPSlot 
                              key={index} 
                              index={index} 
                              className="w-12 h-14 text-xl font-semibold border-gray-200 rounded-xl"
                            />
                          ))}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl btn-hover"
                    disabled={isLoading || otp.length !== 6}
                    data-testid="verify-otp-btn"
                  >
                    {isLoading ? (t.common.loading || 'Verifying...') : t.auth.verifySignIn}
                    <Lock className="w-4 h-4 ml-2" />
                  </Button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setOtp('');
                      setDebugOtp('');
                    }}
                    className="flex items-center justify-center gap-2 w-full text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    data-testid="back-btn"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t.auth.useDifferentEmail}
                  </button>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="text-center text-sm text-gray-500">
            {t.auth.termsText || 'By continuing, you agree to our Terms of Service and Privacy Policy'}
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#064E3B] items-center justify-center p-12">
        <div className="max-w-md space-y-8 text-white">
          <h2 className="text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>
            {t.auth.rightPanelTitle || 'Take control of your subscriptions'}
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mail className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium">{t.auth.scanInbox || 'Scan your inbox'}</h3>
                <p className="text-green-100 text-sm">{t.auth.scanInboxDesc || 'Find forgotten subscriptions automatically'}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <h3 className="font-medium">{t.auth.cancelEase || 'Cancel with ease'}</h3>
                <p className="text-green-100 text-sm">{t.auth.cancelEaseDesc || 'AI-powered cancellation instructions'}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium">{t.auth.privateSecure || 'Private & secure'}</h3>
                <p className="text-green-100 text-sm">{t.auth.privateSecureDesc || 'No bank connection required'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
